/**
 * CardView: the one shape every card renderer reads.
 *
 * Three things can become a card on screen, and CardFrame should not care
 * which: a CardDefinition from the collection, a TokenDefinition, or a live
 * GameObject in a match. Each gets a builder here, and the frame only ever
 * sees a CardView.
 *
 * The live builder is the interesting one. It layers effective stats over the
 * printed ones so the frame can colour a number green when it is buffed and
 * red when it is debuffed, and it carries the battlefield conditions the frame
 * paints: damage, exhausted, frozen, attacking, blocking, attached upgrades.
 *
 * Nothing here branches on a card name or id.
 */

import type {
  CardDefinition,
  CardType,
  Chain,
  Cost,
  Faction,
  GameState,
  Keyword,
  Rarity,
  Subtype,
  TokenDefinition,
} from '@/engine';
import { getCardDef, getEffectiveStats, profile } from '@/engine';

export interface AttachedChip {
  instanceId: string;
  name: string;
  faction: Faction;
}

export interface CardView {
  /** Stable react key and zoom lookup handle. */
  key: string;
  /** Present only for a live object. */
  instanceId?: string;
  /** Present for real cards and live objects made from real cards. */
  cardId?: string;
  tokenId?: string;

  name: string;
  faction: Faction;
  type: CardType;
  subtypes: Subtype[];
  cost: Cost & { x?: boolean };
  text: string;
  flavor?: string;
  artPath?: string;

  /** null for non-characters. */
  attack: number | null;
  health: number | null;
  /** Printed values, so the frame can tell buffed from debuffed. */
  baseAttack: number | null;
  baseHealth: number | null;

  keywords: Keyword[];
  rarity: Rarity | null;
  set: string;
  collectorNumber: number | null;
  legendary: boolean;
  isToken: boolean;
  walletChain?: Chain | 'any';

  /** Battlefield conditions. Absent outside a match. */
  damage: number;
  exhausted: boolean;
  frozen: boolean;
  attacking: boolean;
  blocking: boolean;
  attachments: AttachedChip[];
}

function emptyConditions() {
  return {
    damage: 0,
    exhausted: false,
    frozen: false,
    attacking: false,
    blocking: false,
    attachments: [] as AttachedChip[],
  };
}

export function viewFromCard(def: CardDefinition): CardView {
  const isCharacter = def.type === 'character';
  return {
    key: def.id,
    cardId: def.id,
    name: def.name,
    faction: def.faction,
    type: def.type,
    subtypes: def.subtypes ?? [],
    cost: def.cost ?? {},
    text: def.text ?? '',
    flavor: def.flavor,
    artPath: def.art?.path,
    attack: isCharacter ? def.attack ?? 0 : null,
    health: isCharacter ? def.health ?? 0 : null,
    baseAttack: isCharacter ? def.attack ?? 0 : null,
    baseHealth: isCharacter ? def.health ?? 0 : null,
    keywords: def.keywords ?? [],
    rarity: def.rarity ?? null,
    set: def.set ?? '',
    collectorNumber: def.collectorNumber ?? null,
    legendary: def.legendary === true,
    isToken: false,
    walletChain: def.wallet?.chain,
    ...emptyConditions(),
  };
}

export function viewFromToken(def: TokenDefinition): CardView {
  return {
    key: def.id,
    tokenId: def.id,
    name: def.name,
    faction: def.faction,
    type: 'character',
    subtypes: def.subtypes ?? [],
    cost: {},
    text: def.text ?? '',
    artPath: def.art?.path,
    attack: def.attack,
    health: def.health,
    baseAttack: def.attack,
    baseHealth: def.health,
    keywords: def.keywords ?? [],
    rarity: null,
    set: '',
    collectorNumber: null,
    legendary: false,
    isToken: true,
    ...emptyConditions(),
  };
}

/**
 * A live object. Stats come from getEffectiveStats, so upgrades, statics and
 * temporary buffs are already layered in; the printed values ride along so the
 * frame can colour the difference.
 */
export function viewFromObject(state: GameState, instanceId: string): CardView | null {
  const o = state.objects[instanceId];
  if (!o) return null;
  const prof = profile(state, instanceId);
  const stats = getEffectiveStats(state, instanceId);
  const def = o.cardId ? getCardDef(o.cardId) : undefined;
  const isCharacter = prof.type === 'character';

  const attachments: AttachedChip[] = [];
  const controller = state.players[o.controller];
  if (controller && o.zone === 'battlefield') {
    for (const upId of controller.battlefield.upgrades) {
      const up = state.objects[upId];
      if (!up || up.attachedTo !== instanceId) continue;
      const upProf = profile(state, upId);
      attachments.push({ instanceId: upId, name: upProf.name, faction: upProf.faction });
    }
  }

  return {
    key: instanceId,
    instanceId,
    cardId: o.cardId,
    tokenId: o.tokenId,
    name: prof.name,
    faction: prof.faction,
    type: prof.type,
    subtypes: prof.subtypes ?? [],
    cost: prof.cost ?? {},
    text: prof.text ?? '',
    flavor: def?.flavor,
    artPath: def?.art?.path ?? tokenArtPath(o.tokenId),
    attack: isCharacter ? stats.attack : null,
    health: isCharacter ? stats.health : null,
    baseAttack: isCharacter ? prof.baseAttack : null,
    baseHealth: isCharacter ? prof.baseHealth : null,
    keywords: stats.keywords,
    rarity: def?.rarity ?? null,
    set: def?.set ?? '',
    collectorNumber: def?.collectorNumber ?? null,
    legendary: prof.legendary,
    isToken: prof.isToken,
    walletChain: prof.wallet?.chain,
    damage: o.damage,
    exhausted: o.exhausted,
    frozen: o.frozen,
    attacking: o.attacking === true,
    blocking: typeof o.blocking === 'string',
    attachments,
  };
}

/**
 * Token art lives beside card art. The engine profile does not carry an art
 * path for tokens, so it is looked up lazily to avoid importing src/data here.
 */
let tokenArt: Record<string, string> = {};

/** Called once at startup by src/ui/cards/index.ts with the token art table. */
export function registerTokenArt(table: Record<string, string>): void {
  tokenArt = table;
}

function tokenArtPath(tokenId?: string): string | undefined {
  return tokenId ? tokenArt[tokenId] : undefined;
}

/** Total printed gas cost, for curve charts and sorting. */
export function costTotalOf(cost: Cost): number {
  return (
    (cost.generic ?? 0) +
    (cost.btc ?? 0) +
    (cost.eth ?? 0) +
    (cost.sol ?? 0) +
    (cost.bnb ?? 0) +
    (cost.xrp ?? 0)
  );
}
